import React, { useEffect, useRef } from 'react';
import '../styles/fx-motion.css';

// Page-wide effects for the dashboards and the login page:
// - a soft glow that follows the mouse (behind the content, mouse only)
// - a ring and sparks where you click a button
// - a reading-progress bar at the very top
// - now and then a shooting star crosses the background
// Off for prefers-reduced-motion. Must sit inside an `isolation: isolate`
// parent so the z-index -1 layer stays above that parent's background.
const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const burst = (x, y) => {
  const host = document.createElement('span');
  host.className = 'fx-burst';
  host.style.left = `${x}px`; host.style.top = `${y}px`;
  host.innerHTML = '<i class="fx-burst-ring"></i>' + Array.from({ length: 8 }, (_, i) => `<i class="fx-burst-spark" style="--a:${i * 45}deg"></i>`).join('');
  document.body.appendChild(host);
  setTimeout(() => host.remove(), 700);
};

const MotionFX = () => {
  const spot = useRef(null);
  const bar = useRef(null);

  useEffect(() => {
    if (reduced()) return undefined;
    let raf = 0; let mx = 0; let my = 0;
    const paint = () => {
      raf = 0;
      if (spot.current) spot.current.style.transform = `translate3d(${mx - 260}px, ${my - 260}px, 0)`;
      if (bar.current) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        bar.current.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
      }
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(paint); };
    const onMove = (e) => {
      if (e.pointerType !== 'mouse') return;
      mx = e.clientX; my = e.clientY;
      if (spot.current) spot.current.style.opacity = '1';
      queue();
    };
    const onDown = (e) => {
      const t = e.target.closest?.('button, a, [role="button"], [role="tab"]');
      if (t && !t.disabled) burst(e.clientX, e.clientY);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    queue();
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div className="fx-layer" aria-hidden="true">
        <div ref={spot} className="fx-spot" />
        <span className="fx-shoot" style={{ top: '12%', left: '55%' }} />
        <span className="fx-shoot" style={{ top: '30%', left: '85%', animationDelay: '6s' }} />
        <span className="fx-shoot" style={{ top: '6%', left: '30%', animationDelay: '12s' }} />
      </div>
      <div className="fx-progress" aria-hidden="true"><div ref={bar} /></div>
    </>
  );
};

export default MotionFX;
